import React, { useCallback, useEffect, useState } from "react";
import {
  Shield, ShieldCheck, ShieldOff, Copy, CheckCircle, AlertCircle,
  Loader2, RefreshCw, KeyRound, Download,
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

interface StatusResp { enrolled: boolean; enabled: boolean; last_used_at: string | null; }

interface SetupResp {
  secret: string;
  otpauth_uri: string;
  qr_png_data_uri: string;
  backup_codes: string[];
  issuer: string;
  label: string;
}

const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TwoFactorSetup: React.FC = () => {
  const [status, setStatus] = useState<StatusResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Setup flow state
  const [setupData, setSetupData] = useState<SetupResp | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [savedBackup, setSavedBackup] = useState(false);

  // Disable flow state
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get<StatusResp>(`${API_URL}/auth/2fa/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load 2FA status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setSetupBusy(true); setError(null); setSetupData(null); setSavedBackup(false); setSetupCode("");
    try {
      const r = await axios.post<SetupResp>(`${API_URL}/auth/2fa/setup`, {}, { headers: authHeaders() });
      setSetupData(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to start 2FA setup");
    } finally { setSetupBusy(false); }
  };

  const verifySetup = async () => {
    if (!setupCode || setupCode.trim().length < 6) { setError("Enter the 6-digit code from your authenticator app"); return; }
    setSetupBusy(true); setError(null);
    try {
      await axios.post(`${API_URL}/auth/2fa/verify`, { code: setupCode.trim() }, { headers: authHeaders() });
      setNotice("2FA is now active on your account.");
      setSetupData(null); setSetupCode("");
      await fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Invalid code");
    } finally { setSetupBusy(false); }
  };

  const submitDisable = async () => {
    setDisableBusy(true); setError(null);
    try {
      await axios.post(`${API_URL}/auth/2fa/disable`,
        { password: disablePwd, code: disableCode.trim() },
        { headers: authHeaders() });
      setNotice("2FA has been disabled.");
      setDisableOpen(false); setDisablePwd(""); setDisableCode("");
      await fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to disable 2FA");
    } finally { setDisableBusy(false); }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setNotice("Copied to clipboard");
    setTimeout(() => setNotice(null), 1500);
  };

  const downloadBackupCodes = () => {
    if (!setupData) return;
    const content =
`Moko Afrika — 2FA backup codes
Account: ${setupData.label}
Generated: ${new Date().toISOString()}

Keep these codes somewhere safe (password manager, printed and locked).
Each code can be used ONCE if you lose access to your authenticator app.

${setupData.backup_codes.map(c => "  " + c).join("\n")}
`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "moko-2fa-backup-codes.txt";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    setSavedBackup(true);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-7 text-white shadow-xl shadow-emerald-500/30"
        style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 55%, #047857 100%)" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-emerald-300/35" />
        <div className="pointer-events-none absolute -left-10 bottom-0 w-48 h-48 rounded-full blur-3xl bg-teal-300/25" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10">
            <Shield className="w-7 h-7 text-white" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-2 ring-1 ring-white/20">
              <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              <span className="text-[11px] font-semibold text-white/90 tracking-wide uppercase">Account security</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Two-factor authentication</h1>
            <p className="text-sm text-white/85 mt-1.5 max-w-2xl leading-relaxed">
              Protect your account with a second factor. Once enabled, you'll enter a 6-digit code
              from your authenticator app (Google Authenticator, 1Password, Authy, etc.) every time
              you sign in.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Status card */}
          <div className={`relative overflow-hidden rounded-2xl p-6 shadow-lg ${
            status?.enabled
              ? "text-white shadow-emerald-500/30"
              : "bg-white border border-slate-200"
          }`}
            style={status?.enabled ? { background: "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)" } : undefined}
          >
            {status?.enabled && (
              <>
                <div className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full blur-2xl bg-emerald-300/40" />
                <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
                  backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                  backgroundSize: "20px 20px",
                }} />
              </>
            )}
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  status?.enabled
                    ? "bg-white/20 backdrop-blur-sm ring-1 ring-white/30 shadow-inner"
                    : "bg-slate-100 ring-1 ring-slate-200"
                }`}>
                  {status?.enabled ? (
                    <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.3} />
                  ) : (
                    <ShieldOff className="w-6 h-6 text-slate-500" strokeWidth={2.1} />
                  )}
                </div>
                <div>
                  <p className={`text-base font-bold tracking-tight ${status?.enabled ? "text-white" : "text-slate-900"}`}>
                    {status?.enabled ? "2FA is active" : "2FA is not enabled"}
                  </p>
                  {status?.enabled ? (
                    <p className="text-xs text-white/80 mt-0.5">
                      {status.last_used_at
                        ? `Last used ${new Date(status.last_used_at).toLocaleString("fr-FR")}`
                        : "Your account is protected by a second factor."}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Add a second layer of security in less than a minute.
                    </p>
                  )}
                </div>
              </div>
              {status?.enabled ? (
                <button
                  onClick={() => setDisableOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm font-semibold hover:bg-white/25 transition-all"
                >
                  Disable 2FA
                </button>
              ) : (
                <button
                  onClick={startSetup}
                  disabled={setupBusy}
                  className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:-translate-y-0.5 transition-all disabled:opacity-50 overflow-hidden group"
                  style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  {setupBusy && <Loader2 className="w-4 h-4 animate-spin relative" />}
                  <span className="relative">{setupData ? "Restart setup" : "Enable 2FA"}</span>
                </button>
              )}
            </div>
          </div>

          {/* Notices */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
            </div>
          )}
          {notice && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5" /> {notice}
            </div>
          )}

          {/* Setup wizard */}
          {setupData && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Step 1 — Scan the QR code</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Open your authenticator app and scan the QR. If you can't scan, enter the secret manually.
                </p>
                <div className="mt-4 flex items-start gap-6">
                  <img src={setupData.qr_png_data_uri} alt="TOTP QR" className="w-40 h-40 border border-slate-200 rounded-lg" />
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Account</label>
                      <p className="text-sm text-slate-800">{setupData.label}</p>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Secret</label>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded break-all">{setupData.secret}</code>
                        <button onClick={() => copy(setupData.secret)} className="p-1 rounded hover:bg-slate-100">
                          <Copy className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h2 className="text-base font-semibold text-slate-900">Step 2 — Save your backup codes</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Each code works ONCE and lets you sign in if you lose your authenticator app.
                  Store them in a password manager or print them — <span className="font-semibold">they won't be shown again</span>.
                </p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {setupData.backup_codes.map((c) => (
                    <code key={c} className="text-xs font-mono bg-slate-100 px-2 py-1.5 rounded text-slate-800 text-center">{c}</code>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    onClick={downloadBackupCodes}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Download className="w-4 h-4" /> Download .txt
                  </button>
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer px-3 py-1.5 rounded-lg border ${
                      savedBackup
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border-amber-300 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={savedBackup}
                      onChange={(e) => setSavedBackup(e.target.checked)}
                      className="w-4 h-4 accent-emerald-600"
                    />
                    <span className="font-medium">
                      {savedBackup ? "Backup codes saved" : "I saved my backup codes (required)"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h2 className="text-base font-semibold text-slate-900">Step 3 — Verify</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Enter the 6-digit code your app shows right now.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="000000"
                    maxLength={16}
                    className="px-3 py-2 border border-slate-200 rounded-lg font-mono text-lg tracking-widest w-40 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    onClick={verifySetup}
                    disabled={setupBusy || !savedBackup || setupCode.trim().length < 6}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 shadow"
                    title={!savedBackup ? "Confirm you saved the backup codes first" : ""}
                  >
                    {setupBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enable 2FA
                  </button>
                </div>
                {setupCode.trim().length >= 6 && !savedBackup && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Almost there — confirm you saved the backup codes in Step 2 to unlock the
                      <span className="font-semibold"> Enable 2FA</span> button.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Disable modal */}
          {disableOpen && (
            <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
                <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-red-600" /> Disable 2FA
                </h2>
                <p className="text-sm text-slate-600">
                  Confirm with your password AND a valid TOTP or backup code.
                </p>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
                  <input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Authenticator code or backup code</label>
                  <input type="text" value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="000000 or XXXX-XXXX"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setDisableOpen(false)}
                    className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                  <button onClick={submitDisable} disabled={disableBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
                    {disableBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Disable
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TwoFactorSetup;
